import {
  EMAIL_STATUS_LABELS,
  EMAIL_STATUSES,
  INSTANTLY_INTEREST_LABELS,
  INSTANTLY_INTERESTS,
  INSTANTLY_LEAD_STATUS_LABELS,
  INSTANTLY_LEAD_STATUSES,
  LEAD_PRIORITIES,
  LEAD_STAGES,
  LEAD_STAGE_LABELS,
  RECOMMENDATIONS,
  type InstantlyLeadState,
  type LeadChanges,
  type LeadDetail,
  type LeadFacets,
  type LeadRow,
  type LeadStage,
  type LeadsResponse,
} from '@platform/contracts';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  Dialog,
  DialogDescription,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  LeadsIllustration,
  SheetContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@platform/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Flag,
  Lock,
  RotateCcw,
  Search,
  SlidersHorizontal,
  StickyNote,
  X,
} from 'lucide-react';
import { type ReactNode, type SubmitEvent, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { CompanyTag, StoreLogo } from '../components/company';
import { fd } from '../components/form';
import { Guard, PageHeader } from '../components/page';
import { ListSkeleton, Loadable } from '../components/states';
import { ApiError, api, errorMessage } from '../lib/api';
import { fmtDateTime, fmtNum, useScopedQuery, useTenantName } from '../lib/console';
import { fmtAgo, fmtDate } from '../lib/format';
import { useMe } from '../lib/queries';
import { useScope } from '../lib/use-scope';
import { TimelineList } from './console';

// ------------------------------------------------------------------ filtros

export interface LeadFilters {
  q: string;
  stage: string;
  minScore: string;
  category: string;
  source: string;
  emailStatus: string;
  pushed: string;
  replied: string;
  priority: string;
  instantlyStatus: string;
  interest: string;
  sort: string;
}

export const EMPTY_FILTERS: LeadFilters = {
  q: '',
  stage: '',
  minScore: '',
  category: '',
  source: '',
  emailStatus: '',
  pushed: '',
  replied: '',
  priority: '',
  instantlyStatus: '',
  interest: '',
  sort: 'created_desc',
};

/** Filtros → querystring de /api/leads (sólo lo que tiene valor). Exportado para test. */
export function leadsQueryString(f: LeadFilters, page: number, pageSize: number): string {
  const p = new URLSearchParams();
  p.set('page', String(page));
  p.set('pageSize', String(pageSize));
  for (const [k, v] of Object.entries(f) as [keyof LeadFilters, string][]) {
    if (v.trim() !== '') p.set(k, v.trim());
  }
  return p.toString();
}

const STAGE_TONE: Partial<Record<LeadStage, 'success' | 'warning' | 'destructive' | 'secondary'>> =
  {
    qualified: 'success',
    replied: 'success',
    rejected: 'secondary',
    archived: 'secondary',
    bounced: 'destructive',
    error: 'destructive',
    unsubscribed: 'warning',
  };

/** Nombres legibles de fuentes y orígenes del email (los valores crudos vienen de los stacks). */
const SOURCE_LABELS: Record<string, string> = {
  legacy_db: 'Buscador automático',
  instantly: 'Campaña',
  ingest_api: 'Integración',
  manual: 'Carga manual',
  maps: 'Google Maps',
  html_extraction: 'Su web',
  html_contact_page: 'Página de contacto de su web',
  hunter_search: 'Hunter',
  apollo_enrichment: 'Apollo',
  none: 'No se encontró',
};
/** Veredicto del análisis en lenguaje de negocio (el valor crudo viene del motor en inglés). */
const RECOMMENDATION_LABELS: Record<string, string> = {
  approve: 'Encaja',
  manual_review: 'Revisar a mano',
  reject: 'No encaja',
};
const recommendationLabel = (v: string | null | undefined) =>
  v ? (RECOMMENDATION_LABELS[v] ?? v) : '—';

const sourceLabel = (v: string | null | undefined) => (v ? (SOURCE_LABELS[v] ?? v) : '—');

function StageBadge({ stage }: { stage: LeadStage }) {
  return <Badge variant={STAGE_TONE[stage] ?? 'default'}>{LEAD_STAGE_LABELS[stage]}</Badge>;
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
}) {
  const id = useId();
  return (
    <div className="flex w-full flex-col gap-1 sm:w-48">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="h-9 rounded-md border border-input bg-card px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">Todos</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ------------------------------------------------------------------ detalle

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      aria-label={title}
      className="flex flex-col gap-2 rounded-xl border bg-card p-4 shadow-card"
    >
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

/** Puntuación 1–10 como cifra + barra corta (la barra da la lectura rápida al recorrer la tabla). */
function ScorePill({ score }: { score: number | null }) {
  if (score === null) return <span className="text-muted-foreground">—</span>;
  const tone = score >= 8 ? 'bg-success' : score >= 6 ? 'bg-chart-1' : 'bg-muted-foreground/60';
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-10 overflow-hidden rounded-full bg-muted" aria-hidden>
        <span
          className={`block h-full rounded-full ${tone}`}
          style={{ width: `${String(score * 10)}%` }}
        />
      </span>
      <span className="w-5 text-right font-medium tabular-nums">{score}</span>
    </span>
  );
}

function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b py-1.5 text-sm last:border-0">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-right font-medium">{v}</dd>
    </div>
  );
}

function useLeadCommand(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { path: string; method: 'PATCH' | 'POST'; body?: unknown }) =>
      api<LeadDetail>(`/api/leads/${id}${req.path}`, { method: req.method, body: req.body ?? {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['console'] });
      toast.success('Cambios guardados');
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'stale') {
        toast.error('Alguien lo cambió mientras lo editabas: te mostramos el valor actual.');
        void qc.invalidateQueries({ queryKey: ['console'] });
      } else toast.error(errorMessage(err));
    },
  });
}

function NotesEditor({ lead, run }: { lead: LeadDetail; run: ReturnType<typeof useLeadCommand> }) {
  const notesId = useId();
  const prioId = useId();
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const notes = fd(f, 'notes').trim();
    const priority = fd(f, 'priority');
    const changes: LeadChanges = {};
    const expected: LeadChanges = {};
    if ((notes || null) !== lead.notes) {
      changes.notes = notes || null;
      expected.notes = lead.notes;
    }
    if ((priority || null) !== lead.priority) {
      changes.priority = (priority || null) as LeadChanges['priority'];
      expected.priority = lead.priority;
    }
    if (Object.keys(changes).length === 0) {
      toast.info('No hay cambios');
      return;
    }
    run.mutate({ path: '', method: 'PATCH', body: { changes, expected } });
  };
  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-2"
      key={`${lead.notes ?? ''}|${lead.priority ?? ''}`}
    >
      <Label htmlFor={notesId}>Notas</Label>
      <Textarea id={notesId} name="notes" defaultValue={lead.notes ?? ''} maxLength={5000} />
      <div className="flex items-end gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor={prioId}>Prioridad</Label>
          <select
            id={prioId}
            name="priority"
            defaultValue={lead.priority ?? ''}
            className="h-9 rounded-md border border-input bg-card px-2 text-sm"
          >
            <option value="">Sin prioridad</option>
            {LEAD_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <Button type="submit" disabled={run.isPending}>
          <StickyNote /> Guardar
        </Button>
      </div>
    </form>
  );
}

function OverridesEditor({
  lead,
  run,
}: {
  lead: LeadDetail;
  run: ReturnType<typeof useLeadCommand>;
}) {
  const sId = useId();
  const rId = useId();
  const pId = useId();
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const score = fd(f, 'score');
    const rec = fd(f, 'rec');
    const pers = fd(f, 'pers').trim();
    const changes: LeadChanges = {};
    const expected: LeadChanges = {};
    const nextScore = score ? Number(score) : null;
    if (nextScore !== lead.scoreOverride) {
      changes.scoreOverride = nextScore;
      expected.scoreOverride = lead.scoreOverride;
    }
    if ((rec || null) !== lead.recommendationOverride) {
      changes.recommendationOverride = (rec || null) as LeadChanges['recommendationOverride'];
      expected.recommendationOverride = lead.recommendationOverride;
    }
    if ((pers || null) !== lead.personalizationOverride) {
      changes.personalizationOverride = pers || null;
      expected.personalizationOverride = lead.personalizationOverride;
    }
    if (Object.keys(changes).length === 0) {
      toast.info('No hay cambios');
      return;
    }
    run.mutate({ path: '', method: 'PATCH', body: { changes, expected } });
  };
  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-2"
      key={JSON.stringify([
        lead.scoreOverride,
        lead.recommendationOverride,
        lead.personalizationOverride,
      ])}
    >
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor={sId}>Puntuación manual</Label>
          <Input
            id={sId}
            name="score"
            type="number"
            min={1}
            max={10}
            defaultValue={lead.scoreOverride ?? ''}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={rId}>Recomendación</Label>
          <select
            id={rId}
            name="rec"
            defaultValue={lead.recommendationOverride ?? ''}
            className="h-9 rounded-md border border-input bg-card px-2 text-sm"
          >
            <option value="">La del análisis</option>
            {RECOMMENDATIONS.map((r) => (
              <option key={r} value={r}>
                {recommendationLabel(r)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Label htmlFor={pId}>Primera línea del email (manual)</Label>
      <Textarea
        id={pId}
        name="pers"
        defaultValue={lead.personalizationOverride ?? ''}
        placeholder={lead.personalization ?? 'El análisis no propuso una primera línea'}
        maxLength={2000}
      />
      <Button type="submit" variant="outline" disabled={run.isPending}>
        Guardar ajustes
      </Button>
    </form>
  );
}

/** Foto del lead en Instantly (una por campaña): estado, interés, contadores y paso alcanzado. */
function InstantlyStateCard({ s }: { s: InstantlyLeadState }) {
  const lastActivity = s.lastTouchAt ?? s.lastRepliedAt ?? s.lastOpenedAt ?? s.lastContactedAt;
  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {s.campaignName ? <span className="font-medium">{s.campaignName}</span> : null}
        <Badge variant={s.status === 'bounced' ? 'destructive' : 'secondary'}>
          {s.status
            ? INSTANTLY_LEAD_STATUS_LABELS[s.status]
            : `Estado ${String(s.statusCode ?? '—')}`}
        </Badge>
        {s.interest && s.interest !== 'none' ? (
          <Badge variant={s.interest === 'not_interested' ? 'outline' : 'success'}>
            {INSTANTLY_INTEREST_LABELS[s.interest]}
          </Badge>
        ) : s.interest === null ? (
          <Badge variant="outline">Interés {String(s.interestCode ?? '')}</Badge>
        ) : null}
      </div>
      <dl>
        <KV k="Aperturas" v={fmtNum(s.opens)} />
        <KV k="Respuestas" v={fmtNum(s.replies)} />
        <KV k="Clics" v={fmtNum(s.clicks)} />
        <KV
          k="Email de la secuencia alcanzado"
          v={
            s.stepReached === null
              ? '—'
              : `${String(s.stepReached)}${s.variantReached !== null ? ` (variante ${String(s.variantReached)})` : ''}`
          }
        />
        <KV k="Último envío" v={fmtDateTime(s.lastContactedAt)} />
        <KV k="Última apertura" v={fmtDateTime(s.lastOpenedAt)} />
        <KV k="Última respuesta" v={fmtDateTime(s.lastRepliedAt)} />
        <KV k="Última actividad" v={fmtDateTime(lastActivity)} />
      </dl>
      <p className="text-xs text-muted-foreground">
        Datos de Instantly actualizados {fmtAgo(s.syncedAt)}.
      </p>
    </div>
  );
}

function LeadPanel({ id }: { id: string }) {
  const me = useMe();
  const tenantName = useTenantName();
  const q = useScopedQuery<LeadDetail>(['lead', id], `/api/leads/${id}`);
  const run = useLeadCommand(id);
  const lead = q.data;
  const tenantPerms = me.clients
    .flatMap((c) => c.tenants)
    .find((t) => t.id === lead?.tenantId)?.permissions;
  const canOverride = me.user.isSuperadmin || (tenantPerms?.includes('leads:override') ?? false);
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
      <div className="mb-4 flex items-start gap-3 sm:pr-10">
        <StoreLogo name={lead?.company ?? lead?.domain ?? 'Lead'} domain={lead?.domain} size="lg" />
        <div className="min-w-0">
          <DialogTitle className="text-lg font-semibold">
            {lead?.company ?? lead?.domain ?? 'Lead'}
          </DialogTitle>
          <DialogDescription className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {lead ? (
              <>
                <StageBadge stage={lead.stage} />
                <CompanyTag name={tenantName(lead.tenantSlug)} slug={lead.tenantSlug} />
                {lead.url ? (
                  <a
                    href={lead.url.startsWith('http') ? lead.url : `https://${lead.url}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    {lead.domain ?? lead.url} <ExternalLink className="size-3" aria-hidden />
                  </a>
                ) : null}
              </>
            ) : null}
          </DialogDescription>
        </div>
      </div>
      <Loadable isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()}>
        {lead ? (
          <div className="flex flex-col gap-6">
            <Section title="¿Encaja contigo?">
              <dl>
                <KV k="Puntuación (1–10)" v={lead.score ?? '—'} />
                {lead.scoreOverride !== null ? (
                  <KV k="Puntuación del análisis" v={lead.qualifierScore ?? '—'} />
                ) : null}
                <KV k="Recomendación" v={recommendationLabel(lead.recommendation)} />
                <KV k="Categoría" v={lead.category ?? '—'} />
                <KV
                  k="Vende en Amazon"
                  v={lead.sellsOnAmazon === null ? '—' : lead.sellsOnAmazon ? 'Sí' : 'No'}
                />
                <KV k="Catálogo estimado" v={lead.productCountEstimate ?? '—'} />
                <KV k="Analizada" v={fmtDateTime(lead.qualifiedAt)} />
              </dl>
              {lead.reasons.length > 0 ? (
                <ul className="list-disc pl-5 text-sm">
                  {lead.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              ) : null}
              {lead.redFlags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {lead.redFlags.map((r) => (
                    <Badge key={r} variant="destructive">
                      {r}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </Section>
            <Section title="Contacto">
              <dl>
                <KV k="Email" v={lead.email ?? '—'} />
                <KV k="¿El email existe?" v={EMAIL_STATUS_LABELS[lead.emailStatus]} />
                <KV k="Dónde se encontró" v={sourceLabel(lead.emailSource)} />
                <KV k="Añadida a la campaña" v={fmtDateTime(lead.pushedAt)} />
              </dl>
              {lead.personalization || lead.personalizationOverride ? (
                <p className="rounded-md bg-muted p-3 text-sm">
                  {lead.personalizationOverride ?? lead.personalization}
                </p>
              ) : null}
            </Section>
            {lead.instantly.length > 0 ? (
              <Section title="En la campaña">
                {lead.instantly.map((st, i) => (
                  <InstantlyStateCard key={`${st.campaignName ?? ''}:${String(i)}`} s={st} />
                ))}
              </Section>
            ) : null}
            {lead.writable ? (
              <>
                <Section title="Notas y prioridad">
                  <NotesEditor lead={lead} run={run} />
                </Section>
                {canOverride ? (
                  <Section title="Ajustes manuales (agencia)">
                    <OverridesEditor lead={lead} run={run} />
                  </Section>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {lead.stage === 'archived' ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        run.mutate({ path: '/unarchive', method: 'POST' });
                      }}
                      disabled={run.isPending}
                    >
                      <ArchiveRestore /> Desarchivar
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (
                          confirm(
                            '¿Archivar este lead? No se borra: puedes recuperarlo cuando quieras.',
                          )
                        ) {
                          run.mutate({ path: '/archive', method: 'POST' });
                        }
                      }}
                      disabled={run.isPending}
                    >
                      <Archive /> Archivar
                    </Button>
                  )}
                  {canOverride ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        run.mutate({ path: '/requeue', method: 'POST' });
                      }}
                      disabled={run.isPending}
                    >
                      <RotateCcw /> Volver a analizar
                    </Button>
                  ) : null}
                </div>
              </>
            ) : (
              <Callout tone="info" icon={<Lock />} title="Solo lectura">
                {lead.sourceSystem !== 'legacy_db'
                  ? 'Este contacto llegó directamente desde la campaña y no se puede editar aquí.'
                  : lead.legacyWritesEnabled
                    ? 'Con tu usuario puedes ver este lead, pero no editarlo.'
                    : 'Para esta empresa todavía no se pueden editar leads desde aquí.'}
              </Callout>
            )}
            {lead.notes && !lead.writable ? (
              <Section title="Notas">
                <p className="text-sm whitespace-pre-line">{lead.notes}</p>
              </Section>
            ) : null}
            <Section title="Emails y actividad">
              <TimelineList items={lead.timeline} />
            </Section>
            {lead.history.length > 0 ? (
              <Section title="Cambios hechos desde el panel">
                <ol className="flex flex-col gap-1 text-xs">
                  {lead.history.map((h) => (
                    <li key={h.id} className="text-muted-foreground">
                      {fmtDateTime(h.at)} · {h.actorEmail ?? 'automático'} · {h.action}
                    </li>
                  ))}
                </ol>
              </Section>
            ) : null}
          </div>
        ) : null}
      </Loadable>
    </div>
  );
}

// ------------------------------------------------------------------ página

const PAGE_SIZE = 50;

export function LeadsPage() {
  const { scope } = useScope();
  const [draft, setDraft] = useState<LeadFilters>(EMPTY_FILTERS);
  const [filters, setFilters] = useState<LeadFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);
  const tenantName = useTenantName();
  const qs = leadsQueryString(filters, page, PAGE_SIZE);
  const q = useScopedQuery<LeadsResponse>(['leads', qs], `/api/leads?${qs}`);
  const facets = useScopedQuery<LeadFacets>(['leads-facets'], '/api/leads/facets');
  const searchId = useId();
  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / PAGE_SIZE)) : 1;
  const exportUrl = useMemo(() => {
    const p = new URLSearchParams(leadsQueryString(filters, 1, PAGE_SIZE));
    p.delete('page');
    p.delete('pageSize');
    p.set('scope', scope);
    return `/api/leads/export.csv?${p.toString()}`;
  }, [filters, scope]);

  const set = (k: keyof LeadFilters) => (v: string) => {
    const next = { ...draft, [k]: v };
    setDraft(next);
    if (k !== 'q') {
      setFilters(next);
      setPage(1);
    }
  };

  return (
    <Guard requires="data:read">
      <PageHeader
        title="Leads"
        description="Las tiendas encontradas para tu negocio, si encajan contigo y en qué punto está cada una. Toca una para ver el detalle."
        actions={
          <Button asChild variant="outline">
            <a href={exportUrl} download>
              <Download /> Descargar (Excel/CSV)
            </a>
          </Button>
        }
      />
      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4">
          <form
            role="search"
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setFilters(draft);
              setPage(1);
            }}
          >
            <Label htmlFor={searchId} className="sr-only">
              Buscar
            </Label>
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id={searchId}
                placeholder="Tienda, web o email…"
                value={draft.q}
                className="h-10 pl-9"
                onChange={(e) => {
                  set('q')(e.target.value);
                }}
              />
            </div>
            <Button type="submit" className="h-10">
              Buscar
            </Button>
          </form>
          <div className="flex flex-wrap gap-3">
            <FilterSelect
              label="Estado"
              value={draft.stage}
              onChange={set('stage')}
              options={LEAD_STAGES.map((s) => ({ value: s, label: LEAD_STAGE_LABELS[s] }))}
            />
            {showMore ? (
              <>
                <FilterSelect
                  label="Puntuación mínima"
                  value={draft.minScore}
                  onChange={set('minScore')}
                  options={[5, 6, 7, 8, 9].map((n) => ({
                    value: String(n),
                    label: `≥ ${String(n)}`,
                  }))}
                />
                <FilterSelect
                  label="Categoría"
                  value={draft.category}
                  onChange={set('category')}
                  options={(facets.data?.categories ?? []).map((c) => ({
                    value: c.value,
                    label: `${c.value} (${String(c.count)})`,
                  }))}
                />
                <FilterSelect
                  label="Fuente"
                  value={draft.source}
                  onChange={set('source')}
                  options={(facets.data?.sources ?? []).map((c) => ({
                    value: c.value,
                    label: `${sourceLabel(c.value)} (${String(c.count)})`,
                  }))}
                />
                <FilterSelect
                  label="Email"
                  value={draft.emailStatus}
                  onChange={set('emailStatus')}
                  options={EMAIL_STATUSES.map((s) => ({ value: s, label: EMAIL_STATUS_LABELS[s] }))}
                />
                <FilterSelect
                  label="Contactada"
                  value={draft.pushed}
                  onChange={set('pushed')}
                  options={[
                    { value: 'true', label: 'Sí' },
                    { value: 'false', label: 'No' },
                  ]}
                />
                <FilterSelect
                  label="Respondió"
                  value={draft.replied}
                  onChange={set('replied')}
                  options={[
                    { value: 'true', label: 'Sí' },
                    { value: 'false', label: 'No' },
                  ]}
                />
                <FilterSelect
                  label="Estado en la campaña"
                  value={draft.instantlyStatus}
                  onChange={set('instantlyStatus')}
                  options={INSTANTLY_LEAD_STATUSES.map((s) => ({
                    value: s,
                    label: INSTANTLY_LEAD_STATUS_LABELS[s],
                  }))}
                />
                <FilterSelect
                  label="Interés"
                  value={draft.interest}
                  onChange={set('interest')}
                  options={INSTANTLY_INTERESTS.map((s) => ({
                    value: s,
                    label: INSTANTLY_INTEREST_LABELS[s],
                  }))}
                />
                <FilterSelect
                  label="Prioridad"
                  value={draft.priority}
                  onChange={set('priority')}
                  options={LEAD_PRIORITIES.map((p) => ({ value: p, label: p }))}
                />
                <FilterSelect
                  label="Orden"
                  value={draft.sort === 'created_desc' ? '' : draft.sort}
                  onChange={(v) => {
                    set('sort')(v || 'created_desc');
                  }}
                  options={[
                    { value: 'score_desc', label: 'Mejor puntuación' },
                    { value: 'activity_desc', label: 'Actividad más reciente' },
                    { value: 'created_asc', label: 'Más antiguas primero' },
                  ]}
                />
              </>
            ) : null}
            <div className="flex items-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9"
                aria-expanded={showMore}
                onClick={() => {
                  setShowMore(!showMore);
                }}
              >
                <SlidersHorizontal /> {showMore ? 'Menos filtros' : 'Más filtros'}
              </Button>
            </div>
          </div>
          {JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS) ? (
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(EMPTY_FILTERS);
                  setFilters(EMPTY_FILTERS);
                  setPage(1);
                }}
              >
                <X /> Limpiar filtros
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
      <Loadable
        isLoading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        skeleton={<ListSkeleton rows={8} />}
      >
        {q.data?.rows.length === 0 ? (
          <EmptyState
            illustration={<LeadsIllustration />}
            title="No hay leads con esos filtros"
            description="Prueba a quitar algún filtro o elige otra empresa arriba."
          />
        ) : q.data ? (
          <>
            <p className="mb-2 text-sm text-muted-foreground tabular-nums" aria-live="polite">
              {fmtNum(q.data.total)} leads · página {page} de {pages}
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tienda</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Puntuación</TableHead>
                  <TableHead className="hidden md:table-cell">Categoría</TableHead>
                  <TableHead className="hidden lg:table-cell">Email</TableHead>
                  <TableHead className="hidden sm:table-cell">Encontrada</TableHead>
                  <TableHead className="hidden w-10 sm:table-cell">
                    <span className="sr-only">Abrir</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.rows.map((r: LeadRow) => (
                  <TableRow
                    key={r.id}
                    className="group cursor-pointer"
                    onClick={() => {
                      setOpen(r.id);
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <StoreLogo
                          name={r.company ?? r.domain ?? '·'}
                          domain={r.domain}
                          size="sm"
                          className="hidden sm:inline-flex"
                        />
                        <div className="min-w-0">
                          <button
                            type="button"
                            className="text-left font-medium hover:underline focus-visible:underline focus-visible:outline-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(r.id);
                            }}
                          >
                            {r.company ?? r.domain ?? r.url ?? '(sin nombre)'}
                          </button>
                          <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                            <CompanyTag name={tenantName(r.tenantSlug)} slug={r.tenantSlug} />
                            {r.priority ? (
                              <span className="inline-flex items-center gap-1">
                                <Flag className="size-3" aria-hidden /> prioridad {r.priority}
                              </span>
                            ) : null}
                            {r.hasNotes ? (
                              <span className="inline-flex items-center gap-1">
                                <StickyNote className="size-3" aria-hidden /> con notas
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StageBadge stage={r.stage} />
                    </TableCell>
                    <TableCell>
                      <ScorePill score={r.score} />
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{r.category ?? '—'}</TableCell>
                    <TableCell className="hidden max-w-64 lg:table-cell">
                      {r.email ? (
                        <>
                          <span className="block truncate" title={r.email}>
                            {r.email}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {EMAIL_STATUS_LABELS[r.emailStatus]}
                          </span>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span title={fmtDate(r.createdAt)}>{fmtAgo(r.createdAt)}</span>
                    </TableCell>
                    <TableCell className="hidden w-10 pr-3 text-right sm:table-cell">
                      <ChevronRight
                        className="inline size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
                        aria-hidden
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <nav aria-label="Paginación" className="mt-3 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => {
                  setPage((p) => p - 1);
                }}
              >
                <ChevronLeft /> Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onClick={() => {
                  setPage((p) => p + 1);
                }}
              >
                Siguiente <ChevronRight />
              </Button>
            </nav>
          </>
        ) : null}
      </Loadable>
      <Dialog
        open={open !== null}
        onOpenChange={(v) => {
          if (!v) setOpen(null);
        }}
      >
        <SheetContent side="right" className="w-[38rem] max-w-full bg-background p-0">
          {open ? <LeadPanel id={open} /> : null}
        </SheetContent>
      </Dialog>
    </Guard>
  );
}
